/**
 * Como o **token do volume** se lê — a regra de agrupamento, num lugar só.
 *
 * <h2>O defeito que isto conserta</h2>
 *
 * A `V14` levou o token de **oito caracteres** de um alfabeto de trinta para
 * **32 hexadecimais** (um UUID v4 sem hífen). A etiqueta não acompanhou: ela
 * agrupava com um `if` que só reconhecia o comprimento antigo —
 *
 * <pre>
 *   token.length === 8 ? `${token.slice(0, 4)}-${token.slice(4)}` : token
 * </pre>
 *
 * — e, com 32 caracteres, o `if` não pegava. O papel saía com **32
 * hexadecimais corridos, sem separação nenhuma**, num campo desenhado para
 * nove. Não era erro de estilo: o token impresso existe para ser **conferido**
 * contra a tela, e uma tira de 32 glifos iguais é exatamente o que não se
 * confere.
 *
 * <h2>A regra: oito grupos de QUATRO, uniformes</h2>
 *
 * <pre>
 *   23d9-6d7d-3bd8-4ead-8b8d-354d-ac0a-ef74
 * </pre>
 *
 * Grupos **uniformes**, e não o desenho canônico do UUID (8-4-4-4-12): o olho
 * conta de quatro em quatro sem pensar, e é isso que evita perder o lugar no
 * meio de um texto longo. O agrupamento irregular do UUID é convenção de
 * máquina; aqui o leitor é uma pessoa de luva (ADR-0006, a emenda de
 * 2026-08-14; feature 1.2.2 R10).
 *
 * <p><b>O hífen é ajuda de leitura, não dado.</b> Ele não entra no QR, e o
 * resolvedor o descarta na entrada junto com a caixa e os espaços
 * (`TokenDeVolume.normalizar`).
 *
 * <h2>Por que ele mora aqui, e não na etiqueta</h2>
 *
 * Mesmo motivo de {@link ./quantidade}: **decisão de apresentação se toma uma
 * vez.** A etiqueta é o primeiro consumidor e não é o único previsto — a ficha
 * do volume ecoa o mesmo token —, e duas regras de agrupamento produziriam duas
 * grafias do mesmo identificador circulando no mesmo galpão. Componente visual
 * nasce em `packages/ui`; regra de leitura, também.
 *
 * <h2>⚠️ A cópia que existe do outro lado, e por que ela existe</h2>
 *
 * O servidor tem a mesma regra em `TokenDeVolume.formatado()`, e **ele não a
 * entrega pronta**: `GET /lotes/{id}/volumes` devolve o token **cru**
 * (`VolumeNaEtiqueta`), como devolve a quantidade crua. Enquanto for assim, a
 * etiqueta não tem o que *consumir* — ela formata. As duas cópias estão
 * declaradas, uma apontando para a outra, e a divergência que importa (o
 * tamanho do grupo) é a mesma constante nos dois lados: **quatro**.
 */

/** De quantos em quantos caracteres o hífen de leitura entra. */
export const GRUPO_DO_TOKEN = 4;

/** Quantos grupos cabem numa linha impressa — ver {@link linhasDoToken}. */
export const GRUPOS_POR_LINHA = 4;

/**
 * `23d96d7d…0aef74` → `["23d9", "6d7d", "3bd8", …]`.
 *
 * <p>Genérico por comprimento **de propósito**. A `V14` substituiu os 24 tokens
 * que existiam e o formato de oito caracteres não é mais reconhecido pelo
 * resolvedor — mas uma tela que recebesse um token curto de um registro
 * histórico tem de **desenhar**, não quebrar. Um resto menor que quatro vira o
 * último grupo, com o tamanho que tiver.
 */
export function agruparToken(token: string): string[] {
  const grupos: string[] = [];
  for (let i = 0; i < token.length; i += GRUPO_DO_TOKEN) {
    grupos.push(token.slice(i, i + GRUPO_DO_TOKEN));
  }
  return grupos;
}

/**
 * A forma de uma linha só — `23d9-6d7d-3bd8-4ead-8b8d-354d-ac0a-ef74`.
 *
 * <p>É o equivalente exato de `TokenDeVolume.formatado()`, para onde couber
 * numa linha: tela, rótulo acessível, texto copiável.
 */
export function tokenAgrupado(token: string): string {
  return agruparToken(token).join("-");
}

/**
 * O token quebrado em linhas para o **papel** — `gruposPorLinha` grupos em cada.
 *
 * <pre>
 *   23d9-6d7d-3bd8-4ead
 *   8b8d-354d-ac0a-ef74
 * </pre>
 *
 * <p><b>A quebra é decidida aqui, e não pelo navegador.</b> Deixar o texto
 * dobrar sozinho põe o corte onde a largura mandar — no meio de um grupo, e
 * num lugar diferente na etiqueta A6 nominal e na reduzida da folha A4. Duas
 * etiquetas do mesmo lote com o token cortado em pontos diferentes é o
 * contrário do que o agrupamento existe para fazer.
 *
 * <p>O hífen entre a última coluna de uma linha e a primeira da seguinte
 * **não é impresso**: a quebra de linha já é a separação, e um hífen solto no
 * fim da linha se lê como caractere do token. Ele não é dado de qualquer forma.
 *
 * <p>Token curto continua saindo em **uma** linha (`K7M4-P2XQ`).
 */
export function linhasDoToken(token: string, gruposPorLinha = GRUPOS_POR_LINHA): string[] {
  const grupos = agruparToken(token);
  const linhas: string[] = [];
  for (let i = 0; i < grupos.length; i += gruposPorLinha) {
    linhas.push(grupos.slice(i, i + gruposPorLinha).join("-"));
  }
  return linhas;
}
